// Presentation only: keep every ledger row available in the group's technical details.
const EVENT_LABELS = {
  STORE_ORDER: 'Mağazada doğrulanmış işlem',
  PURCHASE: 'Satın alma kaydı',
  TRIAL_CONVERSION: 'Ücretli döneme geçiş',
  RENEWAL: 'Abonelik yenilendi',
  RESUBSCRIPTION: 'Yeniden abone olundu',
  REFUND: 'İade kaydı',
  REVOCATION: 'Mağaza hakkı geri aldı',
  SUBSCRIPTION_STARTED: 'Abonelik başladı',
  SUBSCRIPTION_RESTARTED: 'Abonelik yeniden başlatıldı',
  TRIAL_STARTED: 'Ücretsiz deneme başladı',
  TRIAL_CONVERTED: 'Ücretsiz deneme ücretliye döndü',
  RENEWAL_SUCCEEDED: 'Abonelik yenilendi',
  RENEWAL_FAILED: 'Yenileme başarısız',
  PAYMENT_FAILED: 'Ödeme başarısız',
  GRACE_STARTED: 'Ödeme bekleniyor, erişim açık',
  GRACE_ENDED: 'Ek ödeme süresi sona erdi',
  ON_HOLD_STARTED: 'Ödeme bekleniyor, erişim kapalı',
  ON_HOLD_ENDED: 'Ödeme bekleme durumu sona erdi',
  PAYMENT_RECOVERED: 'Abonelik yeniden aktif',
  CANCELLATION_SCHEDULED: 'Otomatik yenileme iptal edildi',
  CANCELLATION_REVERSED: 'Otomatik yenileme yeniden açıldı',
  PAUSE_SCHEDULED: 'Duraklatma planı güncellendi',
  PAUSED: 'Abonelik duraklatıldı',
  RESUMED: 'Abonelik devam ediyor',
  PLAN_CHANGE_SCHEDULED: 'Paket değişikliği planlandı',
  PLAN_CHANGED: 'Paket değişti',
  EXPIRED: 'Aboneliğin süresi doldu',
  TRANSACTION_REFUNDED: 'İade kaydedildi',
  ENTITLEMENT_REVOKED: 'Erişim geri alındı',
  HISTORY_BOUNDARY: 'Eski kayıtların kapsam sınırı',
}

function eventLabel(type) {
  return EVENT_LABELS[type] || type || 'Türü bilinmeyen kayıt'
}

function isGoogle(item) {
  return ['GOOGLE', 'ANDROID'].includes(item.provider)
}

function isPause(item) {
  return item.eventType === 'PAUSED'
    || (isGoogle(item) && item.sourceState === 'SUBSCRIPTION_STATE_PAUSED' && String(item.sourceSubtype) === '10'
      && item.eventType === 'ON_HOLD_STARTED')
}

const CONVERSION_TYPES = ['TRIAL_CONVERSION', 'TRIAL_CONVERTED']
const RENEWAL_TYPES = ['RENEWAL', 'RENEWAL_SUCCEEDED', 'PAYMENT_RECOVERED']

function isSuccessfulRecord(item) {
  return item.kind !== 'TRANSACTION' || item.transactionStatus === 'SUCCEEDED'
}

function isTrialStart(item) {
  return isSuccessfulRecord(item) && item.billingPhase !== 'PAID' && item.trialAfter !== false && (item.eventType === 'TRIAL_STARTED'
    || (['PURCHASE', 'RESUBSCRIPTION'].includes(item.eventType) && item.billingPhase === 'FREE_TRIAL'))
}

function isTrialConversion(item) {
  return isSuccessfulRecord(item) && item.billingPhase !== 'FREE_TRIAL' && item.trialAfter !== true
    && CONVERSION_TYPES.includes(item.eventType)
}

// Older API responses remain readable during a backend/panel rolling release.
function isSystemAdjustment(item) {
  if (item.evidenceKind) return item.evidenceKind === 'SYSTEM_ADJUSTMENT'
  if (item.eventType === 'HISTORY_BOUNDARY') return true
  const sourceType = item.recordedSourceType ?? item.sourceType
  const subtype = item.recordedSourceSubtype ?? item.sourceSubtype

  return item.provider === 'APPLE' && sourceType === 'DID_RENEW'
    && (item.eventType === 'CANCELLATION_REVERSED'
      || (item.eventType === 'PAYMENT_RECOVERED' && subtype !== 'BILLING_RECOVERY'))
}

function correctionIdentity(item) {
  // Never merge merely because two corrections share a date or a masked ID.
  if (!item.recordedAt || !item.transactionIdCopyValue || !item.provider || !item.environment
    || item.eventType === 'HISTORY_BOUNDARY') return null

  return JSON.stringify(['system', item.provider, item.environment, item.transactionIdCopyValue,
    item.recordedAt, item.recordedSource, item.recordedSourceType, item.recordedSourceSubtype,
    item.receivedAt, item.accessBefore, item.accessAfter, item.autoRenewBefore, item.autoRenewAfter])
}

function verifiedMovementIdentity(item) {
  if (isSystemAdjustment(item)) return null

  // Historical imports and later observations can have different receipt dates.
  // Require the full store identity AND actual event/period/product evidence instead.
  if (!isSuccessfulRecord(item) || !item.provider || !item.environment || !item.transactionIdCopyValue
    || !item.productId || !item.occurredAt || !item.periodStartAt || !item.periodEndAt) return null

  return JSON.stringify([item.provider, item.environment, item.transactionIdCopyValue,
    item.productId, item.basePlanId || null, item.occurredAt, item.periodStartAt, item.periodEndAt])
}

function planPeriodLabel(items) {
  const products = [...new Set(items.map(item => item.productId).filter(Boolean))]
  if (products.length !== 1) return ''

  return {
    'com.mobile.adverport.yearly': 'Yıllık',
    'com.mobile.adverport.yearlysub': 'Yıllık',
    'com.mobile.adverport.monthly': 'Aylık',
    'com.mobile.adverport.monthlysub': 'Aylık',
    'com.mobile.adverport.sixmonthly': '6 aylık',
    'com.mobile.adverport.sixmonthlysub': '6 aylık',
  }[products[0]] || ''
}

function renewalObservationIdentity(item) {
  if (isSystemAdjustment(item)) return null
  if (RENEWAL_TYPES.includes(item.eventType) && item.provider && item.environment && item.transactionIdCopyValue
    && item.periodEndAt && item.receivedAt && isSuccessfulRecord(item)) {
    return JSON.stringify(['renewal', item.provider, item.environment, item.transactionIdCopyValue, item.periodEndAt, item.receivedAt])
  }

  return null
}

function groupIdentity(item, trials) {
  if (isSystemAdjustment(item)) return correctionIdentity(item)

  // Masked IDs and equal dates alone are not evidence of a shared transaction.
  if (!item.provider || !item.environment || !item.transactionIdCopyValue) return null
  const identity = [item.provider, item.environment, item.transactionIdCopyValue]
  const verifiedIdentity = verifiedMovementIdentity(item)
  if (verifiedIdentity && trials.ambiguous.has(verifiedIdentity)) return null
  if (verifiedIdentity) {
    if (trials.starts.has(verifiedIdentity) && ['PURCHASE', 'TRIAL_STARTED', 'SUBSCRIPTION_STARTED', 'RESUBSCRIPTION', 'SUBSCRIPTION_RESTARTED'].includes(item.eventType)
      && item.billingPhase !== 'PAID' && item.trialAfter !== false) {
      return `trial-start:${verifiedIdentity}`
    }
    if (trials.conversions.has(verifiedIdentity) && [...CONVERSION_TYPES, ...RENEWAL_TYPES].includes(item.eventType)
      && item.billingPhase !== 'FREE_TRIAL' && item.trialAfter !== true) {
      return `trial-conversion:${verifiedIdentity}`
    }
  }
  const observation = renewalObservationIdentity(item)

  // Keep an existing verified observation group intact when one row lacks its event time.
  if (observation && trials.observedRenewals.has(observation)) return observation
  if (verifiedIdentity) {
    if (RENEWAL_TYPES.includes(item.eventType)) return `renewal-event:${verifiedIdentity}`
    if (['PURCHASE', 'SUBSCRIPTION_STARTED'].includes(item.eventType)) return `purchase-event:${verifiedIdentity}`
    if (['RESUBSCRIPTION', 'SUBSCRIPTION_RESTARTED'].includes(item.eventType)) return `restart-event:${verifiedIdentity}`
  }
  if (isPause(item) && item.occurredAt && item.receivedAt) {
    return JSON.stringify(['pause', ...identity, item.occurredAt, item.receivedAt,
      item.accessBefore, item.accessAfter])
  }

  return observation
}

function primaryTime(items) {
  if (isSystemAdjustment(items[0]) && items[0].eventType !== 'HISTORY_BOUNDARY') {
    return { value: items[0].recordedAt || items[0].detectedAt, label: items[0].recordedAt ? 'Sistemde düzeltme zamanı' : 'Sistemde son işlenme' }
  }
  if (items[0]?.eventType === 'HISTORY_BOUNDARY') {
    return { value: items[0].effectiveAt, label: 'Geçmiş takibi için referans zamanı' }
  }
  const occurred = items.find(item => item.occurredAt)
  if (occurred) return { value: occurred.occurredAt, label: 'Mağaza olay zamanı' }
  const effective = items.find(item => item.effectiveAt)
  if (effective) {
    const label = effective.effectiveAt === effective.periodEndAt ? 'Kayıttaki dönem sonu' : 'Kayıttaki geçerlilik zamanı'

    return { value: effective.effectiveAt, label, estimated: true }
  }
  const detected = items.find(item => item.detectedAt)

  return { value: detected?.detectedAt, label: 'Sistemde son işlenme', estimated: true }
}

function describeGroup(items) {
  const item = items.find(isPause) || items.find(row => row.accessAfter) || items[0]
  const types = items.map(row => row.eventType)
  const plan = planPeriodLabel(items)
  let title = eventLabel(item.eventType)
  let description = 'Mağazadan veya sistem kontrolünden alınan abonelik kaydı.'
  if (isSystemAdjustment(item) && item.eventType !== 'HISTORY_BOUNDARY') {
    title = 'Abonelik bilgileri mağazayla eşitlendi'
    description = 'Sistemdeki erişim veya yenileme bilgisi mağazanın doğruladığı durumla güncellendi. Bu kayıt ayrı bir satın alma, ödeme veya kullanıcının yenilemeyi açma işlemini kanıtlamaz.'
  } else if (isPause(item)) {
    title = 'Abonelik duraklatıldı'
    description = item.accessAfter === 'ON_HOLD_NO_ACCESS'
      ? 'Duraklatma nedeniyle erişim kapandı.'
      : 'Mağaza aboneliğin duraklatıldığını bildirdi.'
    if (types.includes('ON_HOLD_STARTED')) description += ' Bu kayıttaki “on hold” etiketi ödeme gecikmesi anlamına gelmiyor.'
  } else if (item.kind === 'TRANSACTION' && item.transactionStatus !== 'SUCCEEDED') {
    const record = {
      PURCHASE: 'Satın alma', RENEWAL: 'Yenileme', REFUND: 'İade', REVOCATION: 'Hak geri alma', RESUBSCRIPTION: 'Yeniden abonelik', TRIAL_CONVERSION: 'Ücretli döneme geçiş',
    }[item.eventType] || 'Mağaza işlemi'
    const status = {
      FAILED: 'başarısız', REFUNDED: 'iade edildi', REVOKED: 'geri alındı', PENDING: 'bekliyor',
    }[item.transactionStatus] || item.transactionStatus || 'durum bilinmiyor'
    title = `${record} kaydı · ${status}`
    description = {
      FAILED: 'Mağaza bu işlemi başarısız olarak kaydetmiş.',
      REFUNDED: 'Bu işlem için iade durumu kaydedilmiş.',
      REVOKED: 'Bu işleme bağlı hak mağaza tarafından geri alınmış.',
    }[item.transactionStatus] || 'Bu işlem başarılı olarak doğrulanmamış. İşlem durumu teknik ayrıntılarda gösterilir.'
  } else if (items.some(isTrialStart)) {
    title = `${plan ? `${plan} üyelik için ücretsiz` : 'Ücretsiz'} deneme başladı`
    description = 'Abonelik ücretsiz denemeyle başladı. Mağaza işlem kaydı bu denemeye aittir; ayrı bir ücretli satın alma değildir.'
  } else if (items.some(isTrialConversion)) {
    title = `Deneme bitti, ücretli ${plan ? `${plan.toLocaleLowerCase('tr-TR')} ` : ''}üyelik başladı`
    description = 'Ücretsiz deneme tamamlandı ve mağaza ücretli döneme geçişi doğruladı. Yenileme ve deneme dönüşümü kayıtları aynı işlemi anlatır; ayrı tahsilatlar değildir.'
  } else if (types.includes('RENEWAL') || types.includes('RENEWAL_SUCCEEDED')) {
    title = types.includes('PAYMENT_RECOVERED') ? 'Abonelik yenilendi ve erişim açıldı' : 'Abonelik yenilendi'
    description = 'Mağazanın doğruladığı yenileme kaydı. İşlem ve erişim kayıtları ayrı tahsilatlar değildir.'
  } else if (types.includes('RESUBSCRIPTION') || types.includes('SUBSCRIPTION_RESTARTED')) {
    title = `${plan ? `${plan} üyelik` : 'Abonelik'} yeniden başladı`
    description = 'Mağazada doğrulanan yeniden abonelik. İşlem ve erişimin açılma kayıtları aynı hareketi anlatır.'
  } else if (item.eventType === 'PAYMENT_RECOVERED') {
    description = isGoogle(item) && String(item.sourceSubtype) === '1'
      ? 'Google, aboneliğin yeniden aktif olduğunu bildirdi. Bu bildirim ödeme beklemesinden dönüş veya duraklatmadan devam etme olabilir.'
      : 'Mağaza yeniden aktif bir abonelik bildirdi. Kesin tahsilat zamanı bu durum kaydından çıkarılamaz.'
  } else {
    description = {
      STORE_ORDER: 'Mağaza bu siparişin ödemesini ve dönemini doğruladı. İlk satın alma mı, yenileme mi olduğu eski bildirimler olmadan belirlenemiyor.',
      PURCHASE: 'Mağazada doğrulanmış satın alma kaydı. Geçmiş eksikse bunun kullanıcının ilk satın alımı olduğu kesin değildir.',
      PAUSE_SCHEDULED: 'Duraklatma takvimi değişti. Bu bildirim, erişimin o anda kapandığını göstermez.',
      GRACE_STARTED: 'Mağaza ödemeyi yeniden deniyor. Ek süre boyunca Premium erişimi açık.',
      GRACE_ENDED: 'Mağazanın erişim açık tuttuğu ek ödeme süresi bitti. Sonraki erişim durumu ayrıca gösterilir.',
      ON_HOLD_STARTED: 'Ödeme bekleme durumuna geçildi ve Premium erişimi kapandı.',
      ON_HOLD_ENDED: 'Ödeme bekleme durumu sona erdi. Sonraki erişim durumu ayrıca gösterilir.',
      CANCELLATION_SCHEDULED: 'Gelecek dönem yenilenmeyecek. Mevcut erişimin hemen sona erdiği anlamına gelmez.',
      CANCELLATION_REVERSED: 'Gelecek dönem için otomatik yenileme yeniden açıldı.',
      HISTORY_BOUNDARY: 'Abonelik geçmişini izlemek için mevcut durumdan oluşturulan teknik referans. Bu tarihte satın alma, yenileme veya erişim değişikliği olduğu anlamına gelmez. Daha eski doğrulanmış olaylar sonradan eklenebilir; bu not geçmişin tamamının bulunduğunu garanti etmez.',
      EXPIRED: 'Abonelik süresi sona erdi.',
      PAYMENT_FAILED: 'Ödeme denemesi başarısız oldu. Erişimin sürüp sürmediği erişim alanında gösterilir.',
      RENEWAL_FAILED: 'Yenileme denemesi başarısız oldu. Bu kayıt başarılı tahsilat değildir.',
      REFUND: 'Mağazanın iade kaydı. Erişimdeki değişiklik ayrıca gösterilir.',
      TRANSACTION_REFUNDED: 'İade hareketi kaydedildi. Erişimdeki değişiklik ayrıca gösterilir.',
      ENTITLEMENT_REVOKED: 'Mağaza Premium erişimini geri aldı.',
    }[item.eventType] || description
  }
  const accessItem = items.find(row => row.accessBefore || row.accessAfter) || item
  let { eventType } = item
  if (items.some(isTrialStart)) eventType = 'TRIAL_STARTED'
  else if (items.some(isTrialConversion)) eventType = 'TRIAL_CONVERTED'

  return {
    key: items.map(row => row.rowKey || `${row.kind}-${row.id}`).join(':'),
    items,
    title,
    description,
    time: primaryTime(items),

    // Transaction evidence proves the paid period, not the exact time our stale
    // access projection changed. Its original values remain in technical details.
    accessBefore: accessItem.evidenceKind === 'STORE_TRANSACTION' ? undefined : accessItem.accessBefore,
    accessAfter: accessItem.evidenceKind === 'STORE_TRANSACTION' ? undefined : accessItem.accessAfter,
    system: isSystemAdjustment(item),
    paused: isPause(item),
    eventType,
    backfilled: items.some(row => row.backfilled),
  }
}

function groupTimeline(items = []) {
  const groups = []
  const identities = new Map()
  const trials = {
    starts: new Set(), conversions: new Set(), ambiguous: new Set(), observedRenewals: new Set(),
  }
  const observations = new Map()
  const trialTypes = new Set()
  items.forEach(item => {
    const observation = renewalObservationIdentity(item)
    if (observation) {
      const types = observations.get(observation) || new Set()
      types.add(item.eventType)
      observations.set(observation, types)
      if (types.size > 1) trials.observedRenewals.add(observation)
    }
    const identity = verifiedMovementIdentity(item)
    if (!identity) return
    if (isTrialStart(item)) trials.starts.add(identity)
    if (isTrialConversion(item)) trials.conversions.add(identity)
    const typeKey = `${identity}:${item.eventType}`
    if (trialTypes.has(typeKey)) trials.ambiguous.add(identity)
    trialTypes.add(typeKey)
  })
  items.forEach(item => {
    const identity = groupIdentity(item, trials)
    const existing = identity && identities.get(identity)
    if (existing && !existing.some(row => row.eventType === item.eventType)) {
      existing.push(item)
    } else {
      const rows = [item]
      groups.push(rows)
      if (identity) identities.set(identity, rows)
    }
  })

  return groups.map(describeGroup).sort((a, b) => (b.time.value || 0) - (a.time.value || 0))
}

function accessLabel(value) {
  return {
    ACTIVE: 'Erişim açık',
    PAYMENT_RETRY_WITH_ACCESS: 'Ödeme bekleniyor · erişim açık',
    ON_HOLD_NO_ACCESS: 'Erişim kapalı',
    EXPIRED: 'Süresi doldu · erişim kapalı',
    REVOKED: 'Erişim geri alındı',
    NONE: 'Erişim yok',
  }[value] || value || 'Kaydedilmemiş'
}

function dateFilterMillis(value, endOfDay) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined
  const suffix = endOfDay ? 'T23:59:59.999+03:00' : 'T00:00:00.000+03:00'
  const timestamp = Date.parse(`${value}${suffix}`)

  return Number.isNaN(timestamp) ? undefined : timestamp
}

function periodTransactionTime(period = {}, groups = []) {
  const type = period.transactionType || period.eventType
  const recorded = {
    value: period.occurredAt || null,
    source: 'transaction',
    label: {
      PURCHASE: 'Satın alma zamanı',
      RENEWAL: 'Yenileme zamanı',
      RESUBSCRIPTION: 'Yeniden abonelik zamanı',
      TRIAL_CONVERSION: 'Ücretli döneme geçiş zamanı',
      TRIAL_STARTED: 'Ücretsiz deneme başlangıcı',
    }[type] || 'İşlem zamanı',
  }
  if (recorded.value || type !== 'RENEWAL' || period.transactionStatus !== 'SUCCEEDED'
    || !period.transactionIdCopyValue || !period.provider || !period.environment || !period.periodEndAt) return recorded

  // Use the SAME verified group as the history, not a masked ID, period boundary or receipt time.
  const matches = groups.filter(group => group.items.some(item => item.kind === 'TRANSACTION'
    && item.eventType === type && item.transactionStatus === 'SUCCEEDED'
    && item.provider === period.provider && item.environment === period.environment
    && item.transactionIdCopyValue === period.transactionIdCopyValue
    && item.periodEndAt === period.periodEndAt
    && (item.periodStartAt || null) === (period.periodStartAt || null)))
  if (matches.length !== 1) return recorded
  const group = matches[0]
  const eventTimes = [...new Set(group.items.filter(item => item.occurredAt).map(item => item.occurredAt))]
  if (eventTimes.length !== 1 || group.time.estimated) return recorded

  return {
    value: group.time.value,
    source: 'related_event',
    label: group.items.some(item => item.eventType === 'PAYMENT_RECOVERED')
      ? 'Yenileme ve erişimin açılma olayı' : 'Yenileme olayı',
  }
}

async function loadPeriodTimeline(fetchPage, maxPages = 20) {
  // Read a separate unfiltered snapshot so changing history filters cannot change period dates.
  const rows = new Map()
  let total = null
  for (let page = 0; page < maxPages; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const timeline = await fetchPage(page, 100)
    if (!Array.isArray(timeline?.content) || timeline.number !== page) throw new Error('Invalid timeline page')
    if (total === null) total = timeline.totalElements
    timeline.content.forEach(item => rows.set(`${item.kind}-${item.id}`, item))
    if (timeline.totalElements !== total) return { items: [...rows.values()], complete: false }
    if (page + 1 >= timeline.totalPages) return { items: [...rows.values()], complete: rows.size === total }
    if (!timeline.content.length) break
  }

  return { items: [...rows.values()], complete: false }
}

module.exports = {
  eventLabel, groupTimeline, primaryTime, accessLabel, dateFilterMillis, periodTransactionTime, loadPeriodTimeline,
}
